import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';

export interface StorageStats {
  usedBytes: number;
  limitBytes: number;
  percentUsed: number;
}

/**
 * Fetch storage statistics for the current tenant
 */
async function fetchStorageStats(): Promise<StorageStats | null> {
  try {
    const response = await api.get<{ success: boolean; data: StorageStats }>('/stats/storage');

    // API returns { success: true, data: { usedBytes, limitBytes, percentUsed } }
    if (!response.data) {
      logger.warn('Storage stats API returned no data', response);
      return null;
    }

    // Handle both wrapped and unwrapped responses
    const stats = response.data.data || response.data;

    if (typeof stats.usedBytes !== 'number' || typeof stats.limitBytes !== 'number') {
      logger.warn('Storage stats API returned invalid data structure', stats);
      return null;
    }

    return stats as StorageStats;
  } catch (error) {
    logger.error('Failed to fetch storage stats', error as Record<string, any>);
    return null;
  }
}

/**
 * Hook to get storage statistics
 */
export function useStorageStats() {
  return useQuery({
    queryKey: ['stats', 'storage'],
    queryFn: fetchStorageStats,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Only retry once on failure
  });
}
